import { Encounter, Practitioner } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { extractSpecialtyFromPractitioners } from "../../../shared-components/fhir-sections/reports/specialty-utils";

export type CareTeamRowData = {
  id: string;
  name: string;
  specialty: string;
  phone: string;
  email: string;
  address: string;
  mostRecentAppointment: string;
  mostRecentAppointmentDate: Date | undefined;
  originalData: Smart<Practitioner>;
};

export function getCareTeamRows({ bundle }: { bundle: FhirBundleSdk }): CareTeamRowData[] {
  try {
    const practitioners = bundle.getPractitioners();

    return practitioners
      .map(practitioner => {
        const name = formatPractitionerName(practitioner);
        const specialty = extractSpecialtyFromPractitioners([practitioner]);

        // Extract contact information
        const phone =
          practitioner.telecom
            ?.filter(t => t.system === "phone")
            .map(t => t.value)
            .filter(Boolean)
            .join(", ") || "-";

        const email =
          practitioner.telecom
            ?.filter(t => t.system === "email")
            .map(t => t.value)
            .filter(Boolean)
            .join(", ") || "-";

        const address = formatAddress(practitioner);

        // Find most recent encounter for this practitioner
        const { formattedDate, dateObj } = getMostRecentEncounterDate(practitioner);

        return {
          id: practitioner.id ?? "-",
          name,
          specialty,
          phone,
          email,
          address,
          mostRecentAppointment: formattedDate,
          mostRecentAppointmentDate: dateObj,
          originalData: practitioner,
        };
      })
      .sort((a, b) => {
        // Sort by most recent appointment date (descending)
        if (!a.mostRecentAppointmentDate && !b.mostRecentAppointmentDate) return 0;
        if (!a.mostRecentAppointmentDate) return 1;
        if (!b.mostRecentAppointmentDate) return -1;
        return b.mostRecentAppointmentDate.getTime() - a.mostRecentAppointmentDate.getTime();
      });
  } catch (error) {
    console.warn("Error getting care team rows:", error);
    return [];
  }
}

function formatPractitionerName(practitioner: Smart<Practitioner>): string {
  if (!practitioner.name?.[0]) return "-";

  const name = practitioner.name[0];
  const prefix = name.prefix?.join(" ") ?? "";
  const given = name.given?.join(" ") ?? "";
  const family = name.family ?? "";

  return `${prefix} ${given} ${family}`.trim() || "-";
}

function formatAddress(practitioner: Smart<Practitioner>): string {
  if (!practitioner.address?.[0]) return "-";

  const addr = practitioner.address[0];
  const parts = [addr.line?.join(", "), addr.city, addr.state, addr.postalCode].filter(Boolean);

  return parts.join(", ") || "-";
}

function getMostRecentEncounterDate(practitioner: Smart<Practitioner>): {
  formattedDate: string;
  dateObj: Date | undefined;
} {
  // Find encounters that reference this practitioner via participant
  const encounters = practitioner.getReferencingResources<Encounter>({
    resourceType: "Encounter",
    referenceField: "participant",
  });

  if (encounters.length === 0) {
    return { formattedDate: "-", dateObj: undefined };
  }

  // Find the most recent encounter date
  let mostRecentDate: Date | undefined;

  for (const encounter of encounters) {
    const dateStr = encounter.period?.end ?? encounter.period?.start;
    if (!dateStr) continue;

    const date = new Date(dateStr);
    if (!mostRecentDate || date > mostRecentDate) {
      mostRecentDate = date;
    }
  }

  if (!mostRecentDate) {
    return { formattedDate: "-", dateObj: undefined };
  }

  return {
    formattedDate: dayjs(mostRecentDate).format(ISO_DATE),
    dateObj: mostRecentDate,
  };
}
